import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card } from '@/components/Primitives';
import { TID } from '@/constants/testIds/voice';
import { useToast } from '@/hooks/use-toast';
import { Microphone, UploadSimple, Trash, Waveform, MagicWand } from '@phosphor-icons/react';

const VOICES = ['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'];

const Ivr = () => {
    const [items, setItems] = useState([]);
    const [text, setText] = useState('Dobrý den, vítáme vás. Pro spojení s obchodním oddělením stiskněte 1, pro technickou podporu stiskněte 2.');
    const [voice, setVoice] = useState('alloy');
    const [name, setName] = useState('Uvítání');
    const [busy, setBusy] = useState(false);
    const [transcript, setTranscript] = useState(null);
    const fileRef = useRef();
    const sttRef = useRef();
    const { toast } = useToast();

    const load = async () => {
        const r = await api.get('/media/audio');
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    const generate = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post('/media/tts/generate', { name, text, voice, language: 'cs' });
            toast({ title: 'Hotovo', description: 'Audio bylo vygenerováno.' });
            load();
        } catch (e) {
            toast({ title: 'Chyba TTS', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setBusy(false);
        }
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', file.name);
        try {
            await api.post('/media/audio/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast({ title: 'Nahráno' });
            load();
        } catch (e) {
            toast({ title: 'Chyba uploadu', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setBusy(false);
        }
    };

    const transcribe = async (file) => {
        if (!file) return;
        setBusy(true);
        setTranscript('Přepisuji…');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('language', 'cs');
        try {
            const r = await api.post('/media/stt/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setTranscript(r.data.text);
            toast({ title: 'Přepsáno', description: 'AI Asistent připraven k použití.' });
        } catch (e) {
            setTranscript(null);
            toast({ title: 'Chyba STT', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        } finally {
            setBusy(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm('Smazat audio?')) return;
        await api.delete(`/media/audio/${id}`);
        load();
    };

    return (
        <>
            <PageHeader
                eyebrow="Virtuální Asistentka"
                title="IVR / Audio knihovna"
                description="Vytvořte uvítací hlášky z textu pomocí OpenAI TTS, nahrajte vlastní audio nebo otestujte transkripci přes Whisper."
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card title="TTS Generátor" className="lg:col-span-2">
                    <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="col-span-2">
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Název</label>
                            <input value={name} onChange={(e) => setName(e.target.value)}
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Hlas</label>
                            <select value={voice} onChange={(e) => setVoice(e.target.value)}
                                    data-testid={TID.ivrVoice}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                {VOICES.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                    <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                           style={{ color: 'var(--text-secondary)' }}>Text uvítání</label>
                    <textarea
                        data-testid={TID.ivrText}
                        rows={6}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="w-full mt-1 px-3 py-2 bg-[var(--bg-app)] border text-sm"
                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                    />
                    <div className="mt-3 flex gap-2">
                        <button data-testid={TID.ivrGenerate} onClick={generate} disabled={busy}
                                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                                style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                            <MagicWand size={14} weight="bold" />
                            {busy ? 'Generuji…' : 'Generovat hlas'}
                        </button>
                        <input ref={fileRef} type="file" accept="audio/*" className="hidden"
                               onChange={(e) => upload(e.target.files?.[0])} />
                        <button data-testid={TID.ivrUpload} onClick={() => fileRef.current?.click()} disabled={busy}
                                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] border"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            <UploadSimple size={14} weight="bold" />
                            Nahrát audio
                        </button>
                    </div>
                </Card>

                <Card title="Whisper STT (AI Asistent)">
                    <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                        Otestujte rozpoznávání řeči, které bude později pohánět AI virtuální asistentku.
                    </p>
                    <input ref={sttRef} type="file" accept="audio/*" className="hidden"
                           onChange={(e) => transcribe(e.target.files?.[0])} />
                    <button onClick={() => sttRef.current?.click()} disabled={busy}
                            data-testid="stt-upload-btn"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] border"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        <Microphone size={14} weight="bold" />
                        Vybrat audio k přepisu
                    </button>
                    {transcript && (
                        <div className="mt-3 p-3 text-xs font-mono"
                             style={{ background: '#06080A', borderRadius: 2 }}>
                            {transcript}
                        </div>
                    )}
                </Card>
            </div>

            <div className="mt-6">
                <Card title="Audio knihovna">
                    {items.length === 0 ? (
                        <div className="py-6 text-center text-sm font-mono"
                             style={{ color: 'var(--text-secondary)' }}>
                            Žádná audio aktiva. Vygenerujte první uvítání.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {items.map((a) => (
                                <div key={a.id} className="flex items-center gap-3 px-3 py-2 border"
                                     style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                    <Waveform size={18} color="var(--brand)" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm">{a.name}</div>
                                        <div className="text-[11px] font-mono"
                                             style={{ color: 'var(--text-secondary)' }}>
                                            {a.source} · {a.format} · {Math.round((a.size_bytes || 0) / 1024)} KB
                                            {a.voice && ` · ${a.voice}`}
                                        </div>
                                    </div>
                                    <audio
                                        src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`}
                                        controls
                                        className="h-8"
                                    />
                                    <button onClick={() => del(a.id)}
                                            className="p-1 hover:bg-[var(--bg-surface-hover)]"
                                            data-testid={`audio-delete-${a.id}`}>
                                        <Trash size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>
        </>
    );
};

export default Ivr;
